<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6 text-center">
    <h1
      class="text-start text-3xl font-bold text-gray-900 sm:text-[24px] leading-7 mb-12"
    >
      Edit Permission
    </h1>

    <div
      class="text-base text-gray-800 font-semibold mb-12 flex flex-wrap justify-center gap-x-12 gap-y-2"
    >
      <div>
        Name :
        <span class="font-normal">{{ header.name }}</span>
      </div>
      <div>
        Email :
        <span class="font-normal">{{ header.email }}</span>
      </div>
    </div>

    <!-- Dropdown: sections -->
    <div class="max-w-2xl mx-auto mb-12">
      <SearchableSelect
        v-model="selectedKey"
        :options="optionsForSelect"
        placeholder="Select Permission Group"
      />
    </div>
    <div class="max-w-2xl mx-auto mb-12">
      <div
        class="bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6"
      >
        <div
          class="flex items-center justify-between mb-4 text-sm font-semibold text-gray-700"
        >
          <button type="button" class="underline" @click="checkAll(true)">
            Select all
          </button>
          <button type="button" class="underline" @click="checkAll(false)">
            Unselect all
          </button>
        </div>

        <div class="space-y-3 text-left">
          <ToggleRow
            v-for="perm in filteredPermissions"
            :key="perm.value"
            :label="perm.label"
            :modelValue="isChecked(perm.value)"
            @update:modelValue="(v) => togglePermission(perm.value, v)"
          />
        </div>
      </div>

      <div class="mt-6">
        <button
          type="button"
          :disabled="saving"
          @click.prevent="handleSave"
          class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-60"
          :style="{ backgroundColor: '#5B9AC5' }"
        >
          {{ saving ? "Saving..." : `Save Changes (${selectedCount})` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { h, ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

const MOCK_USER = {
  name: "John Doe",
  email: "john@example.com",
};

const MOCK_SECTIONS = {
  "Users::General": {
    _key: "Users::General",
    label: "General",
    parent: "Users",
    all: [
      "users.view",
      "users.create",
      "users.edit",
      "users.delete",
    ],
    granted: new Set(["users.view", "users.edit"]),
  },
  "Reports::Cars": {
    _key: "Reports::Cars",
    label: "Cars",
    parent: "Reports",
    all: [
      "cars.report.view",
      "cars.report.export",
    ],
    granted: new Set(["cars.report.view"]),
  },
};
const ToggleRow = {
  name: "ToggleRow",
  props: {
    modelValue: { type: Boolean, default: false },
    label: { type: String, required: true },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const onChange = (e) => emit("update:modelValue", e.target.checked);
    return () =>
      h(
        "label",
        {
          class:
            "flex items-center gap-3 select-none cursor-pointer justify-start",
        },
        [
          h("input", {
            type: "checkbox",
            checked: props.modelValue,
            onChange,
            class: "sr-only peer",
          }),
          h(
            "div",
            {
              class:
                "w-11 h-6 rounded-full bg-gray-200 relative transition peer-checked:bg-green-500",
            },
            [
              h("span", {
                class:
                  "absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5",
              }),
            ]
          ),
          h(
            "span",
            { class: "text-sm font-semibold text-gray-800" },
            props.label
          ),
        ]
      );
  },
};
const SearchableSelect = {
  name: "SearchableSelect",
  props: {
    modelValue: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Select..." },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const open = ref(false);
    const search = ref("");
    const hoverIndex = ref(-1);
    const rootEl = ref(null);
    const inputEl = ref(null);

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      const arr = props.options || [];
      if (!q) return arr;
      return arr.filter((o) =>
        String(o.label).toLowerCase().includes(q)
      );
    });

    const label = computed(() => {
      const curr = props.options.find((o) => o.key === props.modelValue);
      return curr ? curr.label : props.placeholder;
    });

    function openList() {
      if (open.value) return;
      open.value = true;
      nextTick(() => {
        const idx = filtered.value.findIndex(
          (o) => o.key === props.modelValue
        );
        hoverIndex.value =
          idx >= 0 ? idx : filtered.value.length ? 0 : -1;
        inputEl.value?.focus();
        inputEl.value?.select?.();
      });
    }
    function closeList() {
      open.value = false;
      search.value = "";
      hoverIndex.value = -1;
    }
    function selectAt(index) {
      if (index < 0 || index >= filtered.value.length) return;
      const val = filtered.value[index].key;
      emit("update:modelValue", val);
      closeList();
    }
    function onKeydown(e) {
      if (!open.value) {
        if (["ArrowDown", "Enter", " "].includes(e.key)) {
          e.preventDefault();
          openList();
        }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!filtered.value.length) return;
        hoverIndex.value =
          hoverIndex.value < filtered.value.length - 1
            ? hoverIndex.value + 1
            : 0;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!filtered.value.length) return;
        hoverIndex.value =
          hoverIndex.value > 0
            ? hoverIndex.value - 1
            : filtered.value.length - 1;
      } else if (e.key === "Enter") {
        e.preventDefault();
        selectAt(hoverIndex.value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeList();
      }
    }
    function onDocClick(e) {
      if (!rootEl.value) return;
      if (!rootEl.value.contains(e.target)) closeList();
    }
    onMounted(() => document.addEventListener("click", onDocClick));
    onBeforeUnmount(() =>
      document.removeEventListener("click", onDocClick)
    );

    return {
      rootEl,
      inputEl,
      open,
      search,
      filtered,
      hoverIndex,
      label,
      openList,
      closeList,
      selectAt,
      onKeydown,
    };
  },
  render() {
    const trigger = h(
      "button",
      {
        type: "button",
        class:
          "w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm " +
          "bg-white text-gray-900 flex items-center justify-between gap-3 " +
          "hover:border-gray-400 focus:outline-none",
        onClick: this.open ? this.closeList : this.openList,
        onKeydown: this.onKeydown,
        "aria-haspopup": "listbox",
        "aria-expanded": this.open ? "true" : "false",
      },
      [
        h(
          "span",
          {
            class:
              (this.modelValue ? "text-gray-900" : "text-gray-400") +
              " truncate",
          },
          this.label
        ),
        h("svg", { viewBox: "0 0 20 20", class: "w-4 h-4 shrink-0" }, [
          h("path", { d: "M6 8l4 4 4-4", fill: "currentColor" }),
        ]),
      ]
    );

    const dropdown = this.open
      ? h(
          "div",
          {
            class:
              "absolute z-50 mt-2 w-full rounded-2xl border border-gray-200 " +
              "bg-white shadow-lg overflow-hidden",
          },
          [
            h("div", { class: "p-2 border-b border-gray-200 bg-gray-50" }, [
              h("input", {
                ref: "inputEl",
                value: this.search,
                onInput: (e) => (this.search = e.target.value),
                onKeydown: this.onKeydown,
                type: "text",
                placeholder: "Search...",
                class:
                  "w-full h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none",
              }),
            ]),
            h(
              "ul",
              {
                role: "listbox",
                class: "max-h-64 overflow-auto py-1",
                onKeydown: this.onKeydown,
              },
              this.filtered.length
                ? this.filtered.map((opt, i) =>
                    h(
                      "li",
                      {
                        role: "option",
                        key: opt.key,
                        class:
                          "px-3 py-2 text-sm cursor-pointer flex items-center justify-between " +
                          (i === this.hoverIndex
                            ? "bg-gray-100"
                            : "bg-white") +
                          (opt.key === this.modelValue
                            ? " font-semibold"
                            : " font-normal"),
                        onMouseenter: () => (this.hoverIndex = i),
                        onMousedown: (e) => {
                          e.preventDefault();
                          this.selectAt(i);
                        },
                      },
                      [
                        h("span", { class: "truncate" }, opt.label),
                        opt.key === this.modelValue
                          ? h(
                              "svg",
                              { viewBox: "0 0 20 20", class: "w-4 h-4" },
                              [
                                h("path", {
                                  d: "M7.5 13.5l-3-3 1.4-1.4L7.5 10.7l7-7 1.4 1.4-8.4 8.4z",
                                  fill: "currentColor",
                                }),
                              ]
                            )
                          : null,
                      ]
                    )
                  )
                : [
                    h(
                      "li",
                      {
                        class:
                          "px-3 py-3 text-sm text-gray-500 select-none",
                      },
                      "No results"
                    ),
                  ]
            ),
          ]
        )
      : null;

    return h("div", { ref: "rootEl", class: "relative" }, [
      trigger,
      dropdown,
    ]);
  },
};

export default {
  name: "PermissionEdit",
  components: { ToggleRow, SearchableSelect },

  data() {
    return {
      saving: false,
      header: { name: "", email: "" },
      sections: {},
      selectedKey: "",
      selectedNames: new Set(),
    };
  },

  computed: {
    userId() {
      return this.$route.params.id;
    },
    optionsForSelect() {
      return Object.values(this.sections)
        .map((s) => {
          let label = s.label;
          if (s.parent === "Reports") {
            label = `${label} Report`;
          }
          return { key: s._key, label };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    filteredPermissions() {
      const rec = this.sections[this.selectedKey];
      if (!rec) return [];
      return (rec.all || []).map((full) => ({
        value: full,
        label: this.stripPrefix(full),
      }));
    },
    selectedCount() {
      return this.selectedNames.size;
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    stripPrefix(full) {
      const i = String(full).indexOf(".");
      return i >= 0 ? full.slice(i + 1) : full;
    },

    async fetchData() {
      this.header.name = MOCK_USER.name;
      this.header.email = MOCK_USER.email;
      const sections = {};
      const allSelected = new Set();
      Object.values(MOCK_SECTIONS).forEach((s) => {
        sections[s._key] = {
          _key: s._key,
          label: s.label,
          parent: s.parent,
          all: [...s.all],
          granted: new Set(s.granted),
        };
        s.granted.forEach((p) => allSelected.add(p));
      });

      this.sections = sections;
      this.selectedKey = Object.keys(sections)[0] || "";
      this.selectedNames = new Set(allSelected);
    },

    isChecked(name) {
      return this.selectedNames.has(name);
    },

    togglePermission(name, val) {
      if (val) this.selectedNames.add(name);
      else this.selectedNames.delete(name);
      this.selectedNames = new Set(this.selectedNames);
    },
    checkAll(yes) {
      const list = this.filteredPermissions;
      if (yes) list.forEach((p) => this.selectedNames.add(p.value));
      else list.forEach((p) => this.selectedNames.delete(p.value));
      this.selectedNames = new Set(this.selectedNames);
    },

    async handleSave() {
      this.saving = true;
      try {
        console.log("Selected permissions:", Array.from(this.selectedNames));
        this.$router.replace({ name: "Permission" });
      } catch (e) {
        console.error("handleSave error", e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
